<template>
    <LoadingOverlay :is-loading="isLoading" />
    <div class="card-header">
        <div>
            <h3>{{ title }}</h3>
            <p>{{ subtitle }}</p>
        </div>
        <span
            v-if="props.dataRef?.value?.globalConfig?.ageBandConfig"
            class="badge"
        >{{ badgeText }}</span>
    </div>

    <ag-grid-vue
        ref="gridRef"
        class="ag-theme-quartz"
        :row-data="viewBands"
        :column-defs="columnDefs"
        :default-col-def="{ resizable: true }"
        @cell-editing-started="isConfigDirty = true"
        style="height: 400px; width: 100%;"
    />

    <div class="config-actions">
        <template v-if="!isEditing">
            <button
                v-if="!isDefault && canEdit"
                @click="showDefaultModal = true"
                class="btn-link"
            >
                Reset to Default
            </button>
            <al-button
                v-if="canEdit"
                variant="secondary"
                @click="handleEdit"
            >
                Edit
            </al-button>
        </template>
        <template v-else>
            <button
                v-if="!isDefault && canEdit"
                @click="showDefaultModal = true"
                class="btn-link"
            >
                Reset to Default
            </button>
            <al-button
                variant="secondary"
                @click="handleCancel"
            >
                Cancel
            </al-button>
            <al-button
                variant="primary"
                :disabled="!isConfigDirty"
                @click="showSaveModal = true"
            >
                Save
            </al-button>
        </template>
    </div>
    <SaveConfirmationModal
        :show="showSaveModal"
        @close="showSaveModal = false"
        @confirm="handleSave"
    />

    <ResetConfirmationModal
        :show="showDefaultModal"
        @close="showDefaultModal = false"
        @confirm="handleResetToDefaults"
    />
</template>

<script setup lang="ts">
import {
    ref, computed, watch, type Ref
} from 'vue';
import { useACLs } from '@agyletime/vue-3-common/src/composables/acl';
import { AgGridVue } from '@ag-grid-community/vue3';
import { ModuleRegistry } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import type { ValueGetterParams } from '@ag-grid-community/core';
import type {
    OperationsConfiguration, AgeBand, AgeBandItem, TimeUnit
} from '@/types/OperationsConfiguration';
import type { GridCellParams, EditedBandItem } from '@/types/GridTypes';
import SaveConfirmationModal from './SaveConfirmationModal.vue';
import ResetConfirmationModal from './ResetConfirmationModal.vue';
import LoadingOverlay from './LoadingOverlay.vue';

// Convert API model to view model
const convertToViewBands = (items: AgeBandItem[], defaultAgeBand: string): AgeBand[] => {
    const bands = items.map((item, index) => ({
        id: index + 1,
        name: item.name,
        unitType: item.unitType,
        unitValue: item.unitValue,
        isDefault: false
    }));

    // Add default band as non-editable
    bands.push({
        id: bands.length + 1,
        name: defaultAgeBand,
        unitType: 'days' as TimeUnit,
        unitValue: 0,
        isDefault: true
    });

    return bands;
};

ModuleRegistry.registerModules([ClientSideRowModelModule]);

interface Props {
  title: string;
  subtitle?: string;
  dataRef: Ref<OperationsConfiguration>;
  onEdit: () => void;
  onSave: (editedBands?: EditedBandItem[]) => Promise<void> | void;
  onCancel: () => void;
  onResetToDefaults: () => void;
}

const props = withDefaults(defineProps<Props>(), { subtitle: 'Use the default profile or customise it to your needs.' });

const badgeText = computed(() =>
    props.dataRef?.value?.globalConfig?.ageBandConfig._id ? 'Custom' : 'Default'
);

const isDefault = computed(() => !props.dataRef?.value?.globalConfig?.ageBandConfig._id);

const isConfigDirty = ref(false);

const acl = useACLs();
const canEdit = computed(() => acl.can.edit_operations_configurations);
const isEditing = ref(false);
const gridRef = ref();
const showSaveModal = ref(false);
const showDefaultModal = ref(false);
const isLoading = ref(false);

const viewBands = ref<AgeBand[]>([]);

// Initialize viewBands from props
const initializeViewBands = () => {
    const config = props.dataRef?.value?.globalConfig?.ageBandConfig;
    const items = config?.items || [];
    const defaultAgeBand = config?.defaultAgeBand || '';
    viewBands.value = convertToViewBands(items, defaultAgeBand);
};

// Watch for prop changes
watch(() => props.dataRef?.value?.globalConfig?.ageBandConfig, initializeViewBands, { immediate: true });

const readOnlyColumns = [
    {
        field: 'id', headerName: 'Band', width: 100
    },
    {
        field: 'name', headerName: 'Range', flex: 1
    }
];

const editColumns = [
    {
        field: 'id', headerName: 'Band', width: 100, editable: false
    },
    {
        field: 'unitType',
        headerName: 'Unit',
        width: 120,
        editable: (params: GridCellParams) => !params.data.isDefault,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: [ 'hrs', 'days' ] },
        cellClass: (params: GridCellParams) => params.data.isDefault ? '' : 'editable-cell'
    },
    {
        field: 'startValue',
        headerName: 'Start',
        width: 120,
        editable: false,
        valueGetter: (params: ValueGetterParams<AgeBand>) => {
            const index = params.data.id - 1;
            if (index === 0) return 0;
            const prevBand = viewBands.value[index - 1];
            return prevBand ? prevBand.unitValue : 0;
        }
    },
    {
        field: 'unitValue',
        headerName: 'End',
        flex: 1,
        editable: (params: GridCellParams) => !params.data.isDefault,
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: { min: 1 },
        cellClass: (params: GridCellParams) => params.data.isDefault ? '' : 'editable-cell'
    }
];

const columnDefs = computed(() => isEditing.value ? editColumns : readOnlyColumns);

const handleEdit = () => {
    if (!canEdit.value) return;
    props.onEdit();
    isEditing.value = true;
    isConfigDirty.value = false;
};

const handleSave = async () => {
    showSaveModal.value = false;
    isLoading.value = true;
    try {
        gridRef.value?.api?.stopEditing();
        await props.onSave(viewBands.value);
        isEditing.value = false;
    } finally {
        isLoading.value = false;
    }
};

const handleResetToDefaults = async () => {
    showDefaultModal.value = false;
    isLoading.value = true;
    try {
        await props.onResetToDefaults();
    } finally {
        isLoading.value = false;
    }
};

const handleCancel = () => {
    props.onCancel();
    isEditing.value = false;
};
</script>
<style>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.card-header h3 {
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
}

.card-header p {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.badge {
  background: #f0f0f0;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
}

.config-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.btn-link {
  background: none;
  color: #007bff;
  border: none;
  padding: 8px 16px;
  cursor: pointer;
  text-decoration: underline;
}

:deep(.editable-cell) {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  cursor: pointer;
}

:deep(.editable-cell:hover) {
  background-color: #e9ecef;
}

:deep(.ag-cell-editor input) {
  border: 2px solid #007bff;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 14px;
  outline: none;
}

:deep(.ag-cell-editor select) {
  border: 2px solid #007bff;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 14px;
  outline: none;
  background: white;
}
</style>